import type { RowSuggestion } from '@lib/care-model';
import { MATCH_GROUP_WORDING, SUGGEST_WORDING, TONE_PILL, suggestionTone, type MatchState } from '@lib/match-suggest';
import { clientDef, useClient } from '@lib/clients';

/**
 * PART R (William, 17 Oct 2026 12:50 AM: "I would put a % prob of success"): the suggestion pill, shown on the
 * row (flat and grouped) and at the case top in place of "Unmatched phone/email". A match already waiting for
 * approval shows as "Match pending review" instead. Never a match itself.
 *
 * NO em dashes (house voice rule). ASCII hyphens only.
 */
export function SuggestionPill({
  suggestion, pending, testId,
}: {
  suggestion: RowSuggestion | null | undefined;
  pending?: { memberName: string; memberId: string } | null | undefined;
  testId?: string;
}) {
  const help = SUGGEST_WORDING.pillHelp(clientDef(useClient()).displayName);
  if (pending) {
    return (
      <span className="pill pill-primary" data-testid={testId} data-tone="pending" title={help}>
        {SUGGEST_WORDING.pending(pending.memberName || `#${pending.memberId}`)}
      </span>
    );
  }
  if (!suggestion || !suggestion.memberId) return null;
  const tone = suggestionTone(suggestion.score);
  return (
    <span className={TONE_PILL[tone]} data-testid={testId} data-tone={tone} title={help}>
      {SUGGEST_WORDING.pill(suggestion)}
    </span>
  );
}

/**
 * THE MATCH COLUMN'S CELL (William, 17 Oct 2026 about 12:50 AM: "the matched flag in its own col."): Matched,
 * the match waiting or the suggestion as the pill above (its testId kept, so the row's pill is found where it was
 * always looked for), Ambiguous, or No match.
 */
export function MatchCell({ state, testId, pillTestId }: { state: MatchState; testId: string; pillTestId: string }) {
  const body = (() => {
    switch (state.group) {
      case 'matched':
        return <span className="pill pill-success">{MATCH_GROUP_WORDING.matched}</span>;
      case 'pending':
      case 'suggested-high':
      case 'suggested-low':
        return <SuggestionPill suggestion={state.suggestion} pending={state.pending} testId={pillTestId} />;
      case 'ambiguous':
        return <span className="pill pill-warning">{MATCH_GROUP_WORDING.ambiguous}</span>;
      default:
        return <span className="muted">{MATCH_GROUP_WORDING.none}</span>;
    }
  })();
  return <span data-testid={testId} data-group={state.group}>{body}</span>;
}
